import {
    AuthInterface, AuthorizationSuccessResponse, TokenClaimDto, TokenResponseDto
} from '@tassat/ui-definitions';
import { browserSessionStore } from '@tassat/utility';
import axios from 'axios';
import jwt from 'jsonwebtoken';
import {
    Log, User, UserManager, UserManagerSettings
} from 'oidc-client-ts';
import qs from 'qs';

import { AuthService } from '../AuthService';

export class KeycloakService extends AuthService implements AuthInterface {
    constructor() {
        super();
        this.refreshToken = this.refreshToken.bind(this);
    }

    async configure() {
        const authConfig = await AuthService.getAuthConfig();
        const AUTH_CLIENT_ID = authConfig.AUTH_CLIENT_ID;
        const AUTH_CLIENT_SECRET = authConfig.AUTH_CLIENT_SECRET;
        const AUTH_CLIENT_LOGOUT_URL = authConfig.AUTH_CLIENT_LOGOUT_URL;
        const AUTH_ADMIN_ID = authConfig.AUTH_ADMIN_ID;
        const AUTH_ADMIN_SECRET = authConfig.AUTH_ADMIN_SECRET;
        const AUTH_ADMIN_LOGOUT_URL = authConfig.AUTH_ADMIN_LOGOUT_URL;
        const AUTH_CUSTOM_DOMAIN = authConfig.AUTH_CUSTOM_DOMAIN;
        const AUTH_PROTOCOL = authConfig.AUTH_PROTOCOL;

        const commonConfig: Partial<UserManagerSettings> = {
            response_type: 'code',
            scope: 'openid email',
            response_mode: 'query',
            prompt: 'none',
            automaticSilentRenew: false
        };

        const clientOidcClient = new UserManager({
            client_id: AUTH_CLIENT_ID,
            client_secret: AUTH_CLIENT_SECRET,
            authority: `${AUTH_PROTOCOL}${AUTH_CUSTOM_DOMAIN}`,
            redirect_uri: AuthService.redirectUris.client + '?authorizationViaIframe=true',
            post_logout_redirect_uri: `${AUTH_PROTOCOL}${AUTH_CLIENT_LOGOUT_URL}`,
            ...commonConfig
        });

        const adminOidcClient = new UserManager({
            client_id: AUTH_ADMIN_ID,
            client_secret: AUTH_ADMIN_SECRET,
            authority: `${AUTH_PROTOCOL}${AUTH_CUSTOM_DOMAIN}`,
            redirect_uri: AuthService.redirectUris.admin + '?authorizationViaIframe=true',
            post_logout_redirect_uri: `${AUTH_PROTOCOL}${AUTH_ADMIN_LOGOUT_URL}`,
            ...commonConfig
        });

        return {
            clientOidcClient,
            adminOidcClient
        };
    }

    async login(isAdmin: boolean): Promise<void> {
        try {
            const loginConfig = await AuthService.getAuthConfig();
            const loginUrl = isAdmin ? loginConfig?.AUTH_ADMIN_REDIRECT_URL : loginConfig?.AUTH_CLIENT_REDIRECT_URL;
            window.location.href = `${loginConfig.AUTH_PROTOCOL}${loginUrl}`;
        } catch (err) {
            console.error(err, 'error logging in');
        }
    }

    async handleAuthentication(forAdmin: boolean, authorizationCodeResponse: AuthorizationSuccessResponse): Promise<void> {
        if (import.meta.env.DEV) {
            Log.setLogger(console);
            Log.setLevel(Log.DEBUG);
        }
        let isAdmin: boolean | undefined = forAdmin;
        const { clientOidcClient, adminOidcClient } = await this.configure();

        if (authorizationCodeResponse.code && authorizationCodeResponse.authorizationViaIframe === 'true') {
            try {
                if (forAdmin) {
                    await adminOidcClient.signinSilentCallback();
                } else {
                    await clientOidcClient.signinSilentCallback();
                }
            } catch (error) {
                console.log('Authorization failed during silent login process', error);
            }
        } else if (authorizationCodeResponse.code && authorizationCodeResponse.authorizationViaIframe === 'false') {
            this.removeSessionStorage(true);
            const authConfig = await AuthService.getAuthConfig();

            const clientId = forAdmin ? authConfig.AUTH_ADMIN_ID : authConfig.AUTH_CLIENT_ID;
            const clientSecret = forAdmin ? authConfig.AUTH_ADMIN_SECRET : authConfig.AUTH_CLIENT_SECRET;
            const redirectUri = forAdmin ? `${AuthService.redirectUris.admin}` : `${AuthService.redirectUris.client}`;
            const requestData = {
                grant_type: 'authorization_code',
                client_id: clientId,
                client_secret: clientSecret,
                code: authorizationCodeResponse.code,
                redirect_uri: redirectUri + '?authorizationViaIframe=false'
            };

            try {
                const response = await axios({
                    method: 'POST',
                    url: `${authConfig.AUTH_PROTOCOL}${authConfig.AUTH_CUSTOM_DOMAIN}/protocol/openid-connect/token`,
                    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                    data: qs.stringify(requestData),
                    withCredentials: false
                });

                const tokenResponseDto = this.mapTokenResponseToDto(response.data);
                const decoded = jwt.decode(tokenResponseDto.id_token) as unknown as TokenClaimDto;
                const tokenClaimDto = this.mapTokenClaimsToDto(decoded);
                if (tokenClaimDto.exp) {
                    this.setSessionTokens(tokenResponseDto, tokenClaimDto.exp);
                } else {
                    throw new Error('The Auth token provided seems to be invalid. It\'s missing a critical field called \'exp\' which specifies the token\'s expiry time.');
                }

                isAdmin = await this.validateUser(forAdmin, tokenResponseDto.id_token);

                if (isAdmin !== undefined) {
                    this.redirectToApplication(isAdmin);
                }
            } catch (error) {
                console.error('Authentication failed again with redirect approach', error);
                // Second attempt failed, logout.
                this.logout(isAdmin);
            }
        } else {
            const authConfig = await AuthService.getAuthConfig();

            try {
                const response = forAdmin ? await adminOidcClient.signinSilent() as User : await clientOidcClient.signinSilent() as User;
                browserSessionStore.removeItem(`oidc.user:${authConfig.AUTH_PROTOCOL}${authConfig.AUTH_CUSTOM_DOMAIN}:${isAdmin ? authConfig.AUTH_ADMIN_ID : authConfig.AUTH_CLIENT_ID}`);
                this.removeSessionStorage(true);
                const tokenResponseDto = this.mapTokenResponseToDto(response);
                const decoded = jwt.decode(tokenResponseDto.id_token) as unknown as TokenClaimDto;
                const tokenClaimDto = this.mapTokenClaimsToDto(decoded);
                if (tokenClaimDto.exp) {
                    this.setSessionTokens(tokenResponseDto, tokenClaimDto.exp);
                } else {
                    throw new Error('The Auth token provided seems to be invalid. It\'s missing a critical field called \'exp\' which specifies the token\'s expiry time.');
                }
                isAdmin = await this.validateUser(forAdmin, tokenResponseDto.id_token);

                if (isAdmin !== undefined) {
                    this.redirectToApplication(isAdmin);
                }
            } catch (error) {
                console.error('Authentication failed with silent login process', error);
                console.log('Retrying authentication with redirect approach');

                const authorizationUrl = new URL(`${authConfig.AUTH_PROTOCOL}${authConfig.AUTH_CUSTOM_DOMAIN}/protocol/openid-connect/auth`);
                authorizationUrl.searchParams.set('client_id', isAdmin ? authConfig.AUTH_ADMIN_ID : authConfig.AUTH_CLIENT_ID);
                authorizationUrl.searchParams.set('redirect_uri', isAdmin ? AuthService.redirectUris.admin + '?authorizationViaIframe=false' : AuthService.redirectUris.client + '?authorizationViaIframe=false');
                authorizationUrl.searchParams.set('response_type', 'code');
                authorizationUrl.searchParams.set('scope', 'openid email');
                authorizationUrl.searchParams.set('prompt', 'none');
                authorizationUrl.searchParams.set('response_mode', 'query');

                window.location.replace(authorizationUrl.toString());
            }
        }
    }

    async isAuthenticated(): Promise<boolean> {
        return await super.isAuthenticated(
            this.refreshToken
        );
    }

    async refreshToken(workerInstance: any): Promise<void> {
        await super.refreshToken(
            workerInstance,
            '/protocol/openid-connect/token',
            this.mapTokenResponseToDto,
            this.mapTokenClaimsToDto
        );
    }

    async backchannelLogout(forAdmin: boolean): Promise<void> {
        const config = await AuthService.getAuthConfig();
        const logoutUrl = forAdmin ? config.AUTH_ADMIN_BACKCHANNEL_LOGOUT : config.AUTH_CLIENT_BACKCHANNEL_LOGOUT;

        window.location.href = `${config.AUTH_PROTOCOL}${logoutUrl}`;
    }

    protected mapTokenResponseToDto(tokens: User): TokenResponseDto {
        const tokenResponseDto = new TokenResponseDto();
        tokenResponseDto.access_token = tokens?.access_token;
        tokenResponseDto.id_token = tokens?.id_token as string;
        tokenResponseDto.refresh_token = tokens?.refresh_token as string;
        tokenResponseDto.token_type = tokens?.token_type;
        tokenResponseDto.scope = tokens?.scope as string;

        return tokenResponseDto;
    }

    protected mapTokenClaimsToDto(claims: any): TokenClaimDto {
        const tokenClaimDto = new TokenClaimDto();
        tokenClaimDto.aud = claims?.aud;
        tokenClaimDto.auth_time = claims?.auth_time;
        tokenClaimDto.email = claims?.email;
        tokenClaimDto.exp = claims?.exp;
        tokenClaimDto.iat = claims?.iat;
        tokenClaimDto.iss = claims?.iss;
        tokenClaimDto.sid = claims?.sid;
        tokenClaimDto.sub = claims?.sub;
        tokenClaimDto.userid = claims?.sub;
        tokenClaimDto.username = claims?.username;

        return tokenClaimDto;
    }
}

const keycloakService = new KeycloakService();

export default keycloakService;